import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { useResponsive } from '../utils/useResponsive';
import GarageVehicleJobCard from './GarageVehicleJobCard';
import { formatDateOnlyDisplay, formatMotTimeSlotDisplay } from '../utils/helpers';
import { getEnabledStages, GARAGE_LANE_STAGES, hasGarageLanesEnabled, isVehicleWorkTypeVisible } from '../utils/enabledStages';

const STATUS_STYLES = {
  'In Progress': {
    gradient: 'linear-gradient(90deg, #4f8df3 0%, #73b0ff 100%)',
    pillBg: 'rgba(79, 141, 243, 0.18)',
    pillColor: '#3f63b8'
  },
  'Workshop': {
    gradient: 'linear-gradient(90deg, #4f8df3 0%, #73b0ff 100%)',
    pillBg: 'rgba(79, 141, 243, 0.18)',
    pillColor: '#3f63b8'
  },
  'Sales': {
    gradient: 'linear-gradient(90deg, #f97316 0%, #fb923c 100%)',
    pillBg: 'rgba(249, 115, 22, 0.18)',
    pillColor: '#c2410c'
  },
  'MOT': {
    gradient: 'linear-gradient(90deg, #56c596 0%, #7ee0b3 100%)',
    pillBg: 'rgba(86, 197, 150, 0.22)',
    pillColor: '#279268'
  },
  'Bodyshop': {
    gradient: 'linear-gradient(90deg, #ec4899 0%, #f472b6 100%)',
    pillBg: 'rgba(236, 72, 153, 0.2)',
    pillColor: '#be185d'
  },
  'Tyres': {
    gradient: 'linear-gradient(90deg, #b45309 0%, #d97706 100%)',
    pillBg: 'rgba(180, 83, 9, 0.2)',
    pillColor: '#92400e'
  }
};

const DEFAULT_STATUS_STYLE = {
  gradient: 'linear-gradient(90deg, var(--primary) 0%, #7aa7ff 100%)',
  pillBg: 'rgba(79, 141, 243, 0.18)',
  pillColor: '#3f63b8'
};

/** Tabs: id must match filter state and Overview navigation where applicable */
const STAGE_FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'MOT', label: 'MOT' },
  { id: 'Bodyshop', label: 'Bodyshop' },
  { id: 'Workshop', label: 'Workshop' },
  { id: 'Tyres', label: 'Tyres' },
  { id: 'In Progress', label: 'In progress', title: 'In Progress' },
  { id: 'Sales', label: 'Sales' },
  { id: 'Pending Payment', label: 'Unpaid', title: 'Pending payment (not Sales)' }
];

const formatCurrency = (value) => {
  const amount = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatBookedInDate = (vehicle) => {
  const dateStr = vehicle?.dropOffDate || vehicle?.receivedDate;
  if (!dateStr) return null;
  const formatted = formatDateOnlyDisplay(dateStr, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  return formatted || null;
};

function matchesGarageStatusFilter(veh, filter, getSwimlaneCategoryFn) {
  if (filter === 'all') return true;
  if (filter === 'Pending Payment') {
    return veh.status !== 'Sales' && !veh.paid;
  }
  if (filter === 'In Progress') {
    return veh.status === 'In Progress' || veh.status === 'Awaiting Parts';
  }
  if (['MOT', 'Bodyshop', 'Workshop', 'Tyres'].includes(filter)) {
    return veh.status === filter || getSwimlaneCategoryFn(veh) === filter;
  }
  return veh.status === filter;
}

const formatMotBookedDateTime = (vehicle) => {
  const timeSlot = vehicle?.timeSlot;
  if (!timeSlot) return '';
  const dateStr = vehicle?.dropOffDate || vehicle?.receivedDate;
  return formatMotTimeSlotDisplay(timeSlot, dateStr);
};

const GarageVehicles = ({ onOpenVehicleDetail, initialStatusFilter }) => {
  const { vehicles, updateVehicle, garageInfo } = useApp();
  const { isMobile, isTablet } = useResponsive();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter || 'all');
  const [activeVehicleIndex, setActiveVehicleIndex] = useState(null);
  const searchInputRef = useRef(null);
  const isMobileView = isMobile || isTablet;

  // Sync status filter when navigating from Overview with a specific filter
  useEffect(() => {
    if (initialStatusFilter) {
      setStatusFilter(initialStatusFilter);
    }
  }, [initialStatusFilter]);

  const enabledStages = getEnabledStages(garageInfo);
  const stageFilterTabs = useMemo(() => (
    STAGE_FILTER_TABS.filter((tab) => {
      if (['MOT', 'Bodyshop', 'Workshop', 'Tyres', 'Sales'].includes(tab.id)) {
        return enabledStages.includes(tab.id);
      }
      if (tab.id === 'In Progress' || tab.id === 'Pending Payment') {
        return hasGarageLanesEnabled(garageInfo);
      }
      return true;
    })
  ), [enabledStages, garageInfo]);

  useEffect(() => {
    if (!stageFilterTabs.some((tab) => tab.id === statusFilter)) {
      setStatusFilter('all');
    }
  }, [statusFilter, stageFilterTabs]);

  // Keyboard shortcut Ctrl+K: focus search when event is fired
  useEffect(() => {
    const handler = () => searchInputRef.current?.focus();
    window.addEventListener('focus-garage-search', handler);
    return () => window.removeEventListener('focus-garage-search', handler);
  }, []);

  const getStatusStyle = (status) => {
    return STATUS_STYLES[status] || DEFAULT_STATUS_STYLE;
  };

  const matchesSearch = (veh, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [veh.reg, veh.vehicle, veh.summary, veh.technician, veh.contact, veh.motStatus || '', veh.status]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(q));
  };

  // Resolve which swimlane (MOT/Bodyshop/Workshop/Tyres) a vehicle belongs to; In Progress/Awaiting Parts go under previousStatus
  const getSwimlaneCategory = (veh) => {
    const status = veh.status || '';
    if (status === 'In Progress' || status === 'Awaiting Parts') {
      return veh.previousStatus || 'Workshop';
    }
    return status;
  };

  // Filter vehicles based on search, status filter, and enabled work types (exclude soft-deleted)
  const filteredVehicles = useMemo(() => {
    return vehicles
      .map((veh, idx) => ({ veh, idx }))
      .filter(({ veh }) => {
        if (veh.deletedAt) return false;
        if (!isVehicleWorkTypeVisible(garageInfo, veh)) return false;
        const matchesStatus = matchesGarageStatusFilter(veh, statusFilter, getSwimlaneCategory);
        const matchesSearchTerm = matchesSearch(veh, searchTerm);
        return matchesStatus && matchesSearchTerm;
      })
      .map(({ idx }) => idx);
  }, [vehicles, statusFilter, searchTerm, garageInfo]);

  // Filter out Car Park / archived / collected vehicles (no longer on the lot)
  const garageVehicles = useMemo(() => {
    return filteredVehicles.filter(idx => {
      const veh = vehicles[idx];
      return veh.status !== 'Car Park' && veh.status !== 'Archive' && !veh.collected;
    });
  }, [filteredVehicles, vehicles]);

  // Group vehicles by swimlane category (so In Progress vehicles appear under MOT/Bodyshop/Workshop)
  const groupedVehicles = useMemo(() => {
    const grouped = new Map();
    garageVehicles.forEach((idx) => {
      const veh = vehicles[idx];
      const category = getSwimlaneCategory(veh);
      if (!grouped.has(category)) grouped.set(category, []);
      grouped.get(category).push({ vehicle: veh, originalIndex: idx });
    });

    // Order: enabled MOT/Bodyshop/Workshop/Tyres lanes, then Sales if enabled
    const areaOrder = GARAGE_LANE_STAGES.filter((s) => enabledStages.includes(s));
    const otherStatuses = Array.from(grouped.keys())
      .filter((s) => !areaOrder.includes(s) && s !== 'Sales' && enabledStages.includes(s))
      .sort();
    const orderedStatuses = [
      ...areaOrder.filter((s) => grouped.has(s)),
      ...otherStatuses,
      ...(enabledStages.includes('Sales') && grouped.has('Sales') ? ['Sales'] : [])
    ];

    return orderedStatuses.map((status) => ({
      status,
      items: (grouped.get(status) || []).sort((a, b) => {
        const dateA = a.vehicle.receivedDate || a.vehicle.dropOffDate;
        const dateB = b.vehicle.receivedDate || b.vehicle.dropOffDate;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      })
    })).filter((group) => group.items.length > 0);
  }, [garageVehicles, vehicles, enabledStages]);

  const handleVehicleClick = (index) => {
    setActiveVehicleIndex(index);
    if (onOpenVehicleDetail) {
      onOpenVehicleDetail(index);
    }
  };


  const handleSold = (index, e) => {
    e.stopPropagation();
    const veh = vehicles[index];
    if (!veh) return;

    if (window.confirm(`Mark ${veh.reg} as Sold? The vehicle will be moved to "Car Park" status.`)) {
      const updatedVehicle = {
        ...veh,
        status: 'Car Park',
        paid: true,
        collected: true,
        collectionDate: veh.collectionDate || new Date().toISOString(),
        notes: veh.notes 
          ? `Vehicle sold.\n\n${veh.notes}`
          : 'Vehicle sold.'
      };
      updateVehicle(index, updatedVehicle);
    }
  };

  const handleDepositTaken = (index, e) => {
    e.stopPropagation();
    const veh = vehicles[index];
    if (!veh) return;

    if (window.confirm(`Mark ${veh.reg} as Deposit Taken?`)) {
      const updatedVehicle = {
        ...veh,
        notes: veh.notes 
          ? `Deposit taken.\n\n${veh.notes}`
          : 'Deposit taken.'
      };
      updateVehicle(index, updatedVehicle);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleVehicleClick(index);
    }
  };

  return (
    <section
      className={`vehicle-area ${isMobileView ? 'vehicle-area--mobile' : ''}`}
      id="garageVehiclesSection"
    >
      <div className="print-job-cards-container" id="printJobCardsContainer"></div>
      <div className="vehicle-inner">
        <div className="vehicle-header">
          <div>
            <h2>Garage Vehicles</h2>
          </div>
          <div className="vehicle-controls">
            <label className="search-field">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M10.5 3.75a6.75 6.75 0 1 1 0 13.5 6.75 6.75 0 0 1 0-13.5Zm0 1.5a5.25 5.25 0 1 0 0 10.5 5.25 5.25 0 0 0 0-10.5Zm7.97 11.28 3.15 3.15-1.06 1.06-3.15-3.15 1.06-1.06Z" fill="#4f8df3"/>
              </svg>
              <input
                ref={searchInputRef}
                id="vehicleSearch"
                type="search"
                placeholder={isMobileView ? 'Search reg, make, model...' : 'Search by registration, make, model or technician...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="garage-filter-row">
          <span className="garage-filter-label" id="garage-stage-label">
            Stage
          </span>
          <div
            className="garage-stage-tabs"
            role="tablist"
            aria-labelledby="garage-stage-label"
          >
            {stageFilterTabs.map(({ id, label, title }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={statusFilter === id}
                title={title || undefined}
                className={`garage-stage-tab ${statusFilter === id ? 'garage-stage-tab--active' : ''}`}
                onClick={() => setStatusFilter(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="vehicle-content">
          {garageVehicles.length === 0 ? (
            <div className="empty-state">No vehicles match the current filters.</div>
          ) : statusFilter === 'all' ? (
            <div
              className="swimlanes"
              id="swimlanes"
              data-lane-count={groupedVehicles.length}
            >
              {groupedVehicles.map(({ status, items }) => {
                const styles = getStatusStyle(status);

                return (
                  <div 
                    key={status} 
                    className="swimlane"
                    style={{ borderColor: `${styles.pillColor}33` }}
                  >
                    <div className="swimlane-header">
                      <span 
                        className="swimlane-title"
                        style={{ color: styles.pillColor }}
                      >
                        {status}
                      </span>
                      <span 
                        className="swimlane-count"
                        style={{ color: styles.pillColor }}
                      >
                        {items.length} vehicle{items.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="swimlane-body">
                      {items.length === 0 ? (
                        <div className="swimlane-empty">No vehicles in this view.</div>
                      ) : (
                        items.map(({ vehicle: veh, originalIndex }) => {
                          const isVehicleActive = originalIndex === activeVehicleIndex;

                          const tags = [];
                          tags.push(
                            <span 
                              key="paid" 
                              className={`vehicle-tag ${veh.paid ? 'vehicle-tag--success' : 'vehicle-tag--danger'}`}
                            >
                              {veh.paid ? 'Paid' : 'Payment Pending'}
                            </span>
                          );
                          if (veh.collected) {
                            tags.push(
                              <span key="collected" className="vehicle-tag vehicle-tag--success">
                                Collected
                              </span>
                            );
                          }
                          const secondaryMeta = (
                            <>
                              {veh.status === 'MOT' && veh.timeSlot ? (
                                <span style={{ fontWeight: '600', color: 'var(--primary, #4f8df3)' }}>
                                  Booked: {formatMotBookedDateTime(veh)}
                                </span>
                              ) : formatBookedInDate(veh) ? (
                                <span style={{ fontWeight: '600', color: 'var(--primary, #4f8df3)' }}>
                                  Booked: {formatBookedInDate(veh)}
                                </span>
                              ) : null}
                              <span>{formatCurrency(veh.status === 'Sales' ? (veh.salePrice || veh.cost || 0) : veh.cost)}</span>
                            </>
                          );

                          return (
                            <GarageVehicleJobCard
                              key={veh.id || `idx-${originalIndex}`}
                              veh={veh}
                              isActive={isVehicleActive}
                              onClick={() => handleVehicleClick(originalIndex)}
                              onKeyDown={(e) => handleKeyDown(originalIndex, e)}
                              tags={tags}
                              secondaryMeta={secondaryMeta}
                              actions={
                                isVehicleActive ? (
                                  <div className="vehicle-card-actions">
                                    {veh.status === 'Sales' && (
                                      <>
                                        <button
                                          type="button"
                                          className="sales-sold-btn"
                                          onClick={(e) => handleSold(originalIndex, e)}
                                        >
                                          Sold
                                        </button>
                                        <button
                                          type="button"
                                          className="sales-deposit-btn"
                                          onClick={(e) => handleDepositTaken(originalIndex, e)}
                                        >
                                          Deposit Taken
                                        </button>
                                      </>
                                    )}
                                  </div>
                                ) : null
                              }
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="vehicle-grid" id="vehicleGrid">
              {[...garageVehicles].sort((a, b) => {
                // Sort by date (most recent first)
                const vehA = vehicles[a];
                const vehB = vehicles[b];
                const dateA = vehA.receivedDate || vehA.dropOffDate;
                const dateB = vehB.receivedDate || vehB.dropOffDate;
                
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1; // Put vehicles without dates at the end
                if (!dateB) return -1;
                
                const timeA = new Date(dateA).getTime();
                const timeB = new Date(dateB).getTime();
                
                return timeB - timeA; // Descending order (most recent first)
              }).map((idx) => {
                const veh = vehicles[idx];
                const isVehicleActive = idx === activeVehicleIndex;

                const tags = [];
                tags.push(
                  <span 
                    key="paid" 
                    className={`vehicle-tag ${veh.paid ? 'vehicle-tag--success' : 'vehicle-tag--danger'}`}
                  >
                    {veh.paid ? 'Paid' : 'Payment Pending'}
                  </span>
                );
                if (veh.collected) {
                  tags.push(
                    <span key="collected" className="vehicle-tag vehicle-tag--success">
                      Collected
                    </span>
                  );
                }
                const secondaryMetaGrid = (
                  <>
                    {veh.status === 'MOT' && veh.timeSlot ? (
                      <span style={{ fontWeight: '600', color: 'var(--primary, #4f8df3)' }}>
                        Booked: {formatMotBookedDateTime(veh)}
                      </span>
                    ) : formatBookedInDate(veh) ? (
                      <span style={{ fontWeight: '600', color: 'var(--primary, #4f8df3)' }}>
                        Booked: {formatBookedInDate(veh)}
                      </span>
                    ) : null}
                    <span>{formatCurrency(veh.status === 'Sales' ? (veh.salePrice || veh.cost || 0) : veh.cost)}</span>
                  </>
                );

                return (
                  <GarageVehicleJobCard
                    key={veh.id || `idx-${idx}`}
                    veh={veh}
                    isActive={isVehicleActive}
                    onClick={() => handleVehicleClick(idx)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    tags={tags}
                    secondaryMeta={secondaryMetaGrid}
                    actions={
                      isVehicleActive ? (
                        <div className="vehicle-card-actions">
                          {veh.status === 'Sales' && (
                            <>
                              <button
                                type="button"
                                className="sales-sold-btn"
                                onClick={(e) => handleSold(idx, e)}
                              >
                                Sold
                              </button>
                              <button
                                type="button"
                                className="sales-deposit-btn"
                                onClick={(e) => handleDepositTaken(idx, e)}
                              >
                                Deposit Taken
                              </button>
                            </>
                          )}
                        </div>
                      ) : null
                    }
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GarageVehicles;
